import React from "react";
import PageLayout from "../../../components/Layout/PageLayout";

const ExternalLinkIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    className="inline-block ml-1"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M11.3349 10.3301V5.60547L4.47065 12.4707C4.21095 12.7304 3.78895 12.7304 3.52925 12.4707C3.26955 12.211 3.26955 11.789 3.52925 11.5293L10.3945 4.66504H5.66011C5.29284 4.66504 4.99507 4.36727 4.99507 4C4.99507 3.63273 5.29284 3.33496 5.66011 3.33496H11.9999L12.1337 3.34863C12.4369 3.41057 12.665 3.67857 12.665 4V10.3301C12.6649 10.6973 12.3672 10.9951 11.9999 10.9951C11.6327 10.9951 11.335 10.6973 11.3349 10.3301ZM11.333 4.66699L11.3349 4.66797L11.332 4.66504H11.331L11.333 4.66699Z"></path>
  </svg>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200 font-medium"
  >
    {children}
    <ExternalLinkIcon />
  </a>
);

const CookiePolicyScreen: React.FC = () => {
  return (
    <PageLayout>
      <div className="w-full pt-6 pb-20">
        <header className="mb-6 text-center">
          <h1 className="text-[20px] md:text-[35px] mb-3">Cookie Policy</h1>
          <p className="text-lg text-gray-600">
            Last Updated: November 14, 2025
          </p>
        </header>

        {/* Main Content */}
        <main className="bg-white">
          {/* Introduction */}
          <section className="mb-4">
            <p className="text-gray-700 leading-relaxed">
              This Cookie Policy explains how 25th & Staffing ("we", "us", or
              "our") uses cookies and similar technologies on our website{" "}
              <ExternalLink href="https://www.25thairecruiter.com">
                https://www.25thairecruiter.com
              </ExternalLink>
              .
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-4">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. What are cookies?
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Cookies are small text files that are placed on your device
              (computer, smartphone, or tablet) when you visit a website. They
              are widely used to make websites work, or work more efficiently,
              as well as to provide information to the owners of the site.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-4">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. How we use cookies
            </h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We use cookies for several purposes:
            </p>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <span className="text-gray-500 mr-3">●</span>
                <div>
                  <span className="font-semibold text-gray-900">
                    Strictly Necessary Cookies:
                  </span>{" "}
                  These cookies are essential for you to browse the website and
                  use its features, such as accessing secure areas of the site.
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-3">●</span>
                <div>
                  <span className="font-semibold text-gray-900">
                    Performance & Analytics Cookies:
                  </span>{" "}
                  These cookies collect information about how you use our
                  website, like which pages you visit most often. This data
                  helps us optimize our website and make it easier to navigate.
                  We use services like{" "}
                  <ExternalLink href="https://analytics.google.com">
                    Google Analytics
                  </ExternalLink>{" "}
                  for this purpose.
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-3">●</span>
                <div>
                  <span className="font-semibold text-gray-900">
                    Functional Cookies:
                  </span>{" "}
                  These cookies allow our website to remember choices you make
                  (such as your user name or the region you are in) and provide
                  enhanced, more personal features.
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-3">●</span>
                <div>
                  <span className="font-semibold text-gray-900">
                    Targeting & Marketing Cookies:
                  </span>{" "}
                  These cookies are used to deliver marketing communications
                  that are more relevant to you and your interests. They may be
                  used by us or by third-party advertising partners.
                </div>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-4">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. Your Choices
            </h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              When you first visit our website, you will see a cookie banner
              asking for your consent to use non-essential cookies.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You can also control and/or delete cookies as you wish. Most web
              browsers allow you to manage your cookie preferences through their
              settings. You can set your browser to block cookies or to alert
              you when cookies are being sent. Please note that if you block all
              cookies (including essential cookies), you may not be able to
              access all or parts of our site.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-4">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Changes to this Policy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Cookie Policy from time to time. We will notify
              you of any changes by posting the new policy on this page.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about our use of cookies, please contact
              us at:{" "}
              <ExternalLink href="mailto:privacy@25thmail.com">
                privacy@25thmail.com
              </ExternalLink>
              .
            </p>
          </section>
        </main>
      </div>
    </PageLayout>
  );
};

export default CookiePolicyScreen;
